import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useOnboardingState, type MetaOauthOutcome } from '@/contexts/OnboardingContext';
import { ApiError } from '@/lib/api';
import { metaOauthService } from '@/services/metaOauthService';

/** Route ``/onboarding/meta/callback`` — terminal landing page from Meta's
 * consent screen. Reads ``?code``/``?state``/``?error`` from the URL,
 * posts to the backend, stores the outcome in
 * :func:`useOnboardingState`, then navigates back to ``/bot`` (F031
 * onboarding timeline) which renders the appropriate Section state.
 */
export function MetaCallbackPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setMetaOauthOutcome } = useOnboardingState();
  // React 18 StrictMode double-invokes effects in dev — guard against a
  // double POST that would consume the nonce twice and surface as
  // ``oauth_state_invalid`` on the legitimate second user.
  const dispatchedRef = useRef(false);

  useEffect(() => {
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;

    const error = params.get('error');
    if (error === 'access_denied') {
      setMetaOauthOutcome({ kind: 'error_denied' });
      navigate('/bot', { replace: true });
      return;
    }

    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      setMetaOauthOutcome({
        kind: 'error_unknown',
        message: t('onboarding.metaOauth.error_unknown.body'),
      });
      navigate('/bot', { replace: true });
      return;
    }

    metaOauthService
      .completeCallback(code, state)
      .then((payload) => {
        setMetaOauthOutcome({ kind: 'connected', payload });
      })
      .catch((err) => {
        const outcome = mapErrorToOutcome(err, t('onboarding.metaOauth.error_unknown.body'));
        setMetaOauthOutcome(outcome);
      })
      .finally(() => {
        navigate('/bot', { replace: true });
      });
  }, [navigate, params, setMetaOauthOutcome, t]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <p>{t('onboarding.metaOauth.callback_processing')}</p>
      </div>
    </div>
  );
}

function mapErrorToOutcome(err: unknown, fallbackMessage: string): MetaOauthOutcome {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'oauth_state_expired':
        return { kind: 'error_state_expired' };
      case 'oauth_state_invalid':
        return { kind: 'error_state_expired' };
      case 'meta_exchange_failed':
        return { kind: 'error_transient' };
      case 'meta_transient':
        return { kind: 'error_transient' };
      default:
        return { kind: 'error_unknown', message: err.message || fallbackMessage };
    }
  }
  return { kind: 'error_unknown', message: fallbackMessage };
}
